import { apiFetch, isApiMode } from './http';

// ─────────────────────────────────────────────────────────────────────
// WHAT THIS DEPLOYMENT OWNS (slice 5d, docs/domains/MESSAGING.md).
//
// The browser has to know whether the server owns messaging, and there must
// be exactly ONE source for that. A `VITE_` flag baked into the bundle would
// be a second — and two flags that can disagree are worse than none, because
// the disagreement is silent and its shape is «half the cutover»: a client
// writing a blob the API refuses, or reading an inbox nothing fills.
//
// So the deployment answers `GET /config` and this module asks, once.
//
// ── THE DEFAULT IS THE LOUD DIRECTION ───────────────────────────────
//
// When the answer cannot be obtained — no API mode, a failed fetch, a
// deployment too old to have the endpoint — this reports NOT authoritative.
// That is not the optimistic choice; it is the one whose failure is visible:
//
//   wrongly «not authoritative»  the client uses the blob, the API answers
//                                410, and messaging fails in the open.
//   wrongly «authoritative»      the client reads an empty inbox from an
//                                empty store and shows nothing. Working
//                                software with no messages in it, which
//                                nobody reports.
//
// One of those gets fixed the same afternoon. The other is discovered weeks
// later by somebody asking where their conversation went.
// ─────────────────────────────────────────────────────────────────────

export interface DeploymentConfig {
  messaging: { serverAuthoritative: boolean };
  /**
   * Whether `/sa` should OFFER the dev sign-in form (#445).
   *
   * Not a capability and not a credential — the form needs a password that
   * lives in Infisical. This says whether the deployment is one where that
   * password means anything, so the portal does not render a control that
   * answers 404.
   */
  staffLogin: { devFormEnabled: boolean };
  /**
   * Whether EVERY signed-in user may file a bug report (owner decision
   * 2026-10-22) — so the member app and the floating button offer the form
   * only where the server would accept it. Who ADMINISTERS reports is not
   * here (it names people, and this document is public); `/me` says it, for
   * the caller only.
   */
  bugReports: { open: boolean };
}

/** What a deployment that cannot answer is treated as. See above. */
const UNKNOWN: DeploymentConfig = {
  messaging: { serverAuthoritative: false },
  staffLogin: { devFormEnabled: false },
  // Unknown is CLOSED: offering a form the server then refuses is the worse
  // failure, and an administrator is told by `/me` regardless.
  bugReports: { open: false },
};

/**
 * Cached for the life of the page.
 *
 * The switch cannot change while a deployment runs — that is the whole point
 * of it being read once at startup on the server — so re-asking is a request
 * per operation for an answer that cannot move. The in-flight promise is
 * cached too, so a burst at boot makes one request rather than one each.
 */
let pending: Promise<DeploymentConfig> | null = null;

export function resetDeploymentConfig(): void {
  pending = null;
}

export function loadDeploymentConfig(): Promise<DeploymentConfig> {
  if (!pending) {
    pending = (async () => {
      if (!isApiMode()) return UNKNOWN;
      try {
        const res = await apiFetch('/config');
        if (!res.ok) return UNKNOWN;
        const body = (await res.json()) as Partial<DeploymentConfig> | null;
        // READ DEFENSIVELY. An older deployment answers 404 and a newer one
        // may answer a shape this bundle does not know; neither may be read
        // as «yes».
        return {
          messaging: {
            serverAuthoritative: body?.messaging?.serverAuthoritative === true,
          },
          staffLogin: {
            devFormEnabled: body?.staffLogin?.devFormEnabled === true,
          },
          bugReports: {
            open: body?.bugReports?.open === true,
          },
        };
      } catch {
        return UNKNOWN;
      }
    })();
  }
  return pending;
}

/**
 * Does the SERVER own messaging?
 *
 * Asynchronous on purpose. The honest answer requires having asked, and a
 * synchronous accessor would have to guess for every caller that runs before
 * the first answer arrives — which is exactly the silent half-cutover this
 * module exists to prevent.
 */
export async function messagingIsServerAuthoritative(): Promise<boolean> {
  return (await loadDeploymentConfig()).messaging.serverAuthoritative;
}
